import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'

import { IPermitTypeField } from '~/client/graph'
import StruxhubTextArea from '~/client/src/shared/components/StruxhubInputs/StruxhubTextArea'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'

import SitePermitCreationFormStore from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'
import PermitFieldRemoveIcon from './PermitFieldRemoveIcon'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number
}

@observer
export default class PermitFormTextAreaField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      isViewMode,
      typeField,
      fieldsStore,
      store,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        store={store}
        fieldsStore={fieldsStore}
        componentRenderer={this.renderComponent}
        onAddFieldClick={this.addNewFieldValue}
        onRemoveField={this.removeValue}
      />
    )
  }

  private renderComponent = (
    value: string,
    index: number,
    isHeaderShown?: boolean,
  ): JSX.Element => {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const { caption, isMandatory, isMultiple } = typeField
    const { isFieldChanged } = fieldsStore
    const shouldShowRemoveBtn = !!index || !!value

    return (
      <div className="row y-center">
        <StruxhubTextArea
          className="overflow-hidden"
          label={caption}
          isRequired={isMandatory}
          isChanged={isFieldChanged(
            typeField.id,
            index,
            tableId,
            tableRowIndex,
            parentInstanceId,
            parentValueIndex,
          )}
          isLabelHidden={!!index && !isHeaderShown}
          isHelperTextCondensed={isMultiple && !isHeaderShown}
          isTopMarginForced={isMultiple && !index}
          value={value}
          onChange={this.onValueChange.bind(this, index)}
        />
        {isMultiple && shouldShowRemoveBtn && !isHeaderShown && (
          <PermitFieldRemoveIcon index={index} onClick={this.removeValue} />
        )}
      </div>
    )
  }

  private get fieldValues(): string[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    const values = fieldsStore.getFieldValues<string>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : ['']
  }

  private onValueChange(
    fieldIndex: number,
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const { changeFieldValue, changeTableFieldValue } = fieldsStore

    if (this.isTableField) {
      changeTableFieldValue(
        tableId,
        tableRowIndex,
        typeField,
        fieldIndex,
        event.target.value,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    changeFieldValue(
      typeField,
      fieldIndex,
      event.target.value,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private addNewFieldValue = () => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.addNewFieldValue(
        typeField,
        '',
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private removeValue = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.removeFieldValue(
        typeField.id,
        index,
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private get isTableField(): boolean {
    return !!this.props.tableId
  }
}
